import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

export type ListItemProps<T> = {
    item: T | undefined;
    loading?: boolean;
};

export type ListProps<T> = {
    data: T[];
    children: (props: ListItemProps<T>) => React.ReactNode;
    rowCount: number;
    onEndReached?: () => void;
    endReachedThreshold?: number;
    loading?: boolean;
    defaultHeight?: number;
    overscanRowCount?: number;
};

const List = <T extends any>({
    data,
    children,
    rowCount,
    onEndReached,
    endReachedThreshold = 150,
    loading,
    defaultHeight = 50,
    overscanRowCount = 3,
}: ListProps<T>) => {
    const parentRef = useRef<HTMLDivElement | null>(null);

    const virtualizer = useVirtualizer({
        count: rowCount || data.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => defaultHeight,
        overscan: overscanRowCount,
        measureElement:
            typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
    });

    return (
        <div
            ref={parentRef}
            style={{ overflowY: 'auto', height: '100%', width: '100%' }}
            onScroll={(e) => {
                const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
                if (scrollHeight - scrollTop - clientHeight < endReachedThreshold) {
                    onEndReached?.();
                }
            }}
        >
            <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
                {virtualizer.getVirtualItems().map((virtualRow) => (
                    <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                        }}
                    >
                        {children({
                            item: data[virtualRow.index],
                            loading: loading && !data[virtualRow.index],
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default List;
